import { createSelector } from "@reduxjs/toolkit";
import { has, isEmpty, isNil } from "lodash";
import { selectCurrentWorkspaceId } from "slices/addIn/authentication/selectors";
import { REQUIRED_METADATA_VALUES } from "utils/add-in/utilities";

export const EMPTY = {};

export const selectMetadataState = (state) => state?.addIn?.metadata;

export const selectIsMetadataInitialized = createSelector(
  [selectMetadataState],
  (metadata) => Boolean(metadata.isInitialized)
);

export const selectOfficeFileName = createSelector(
  [selectMetadataState],
  (metadata) => {
    const { fileName, fileType } = metadata || {};
    if (!fileName || !fileType) return "";
    return `${fileName}.${fileType}`;
  }
);

export const selectDocumentMetadata = createSelector(
  [selectMetadataState],
  (metadata) => metadata.properties || EMPTY
);

export const selectAddInDocumentId = createSelector(
  [selectDocumentMetadata],
  (properties) => properties.documentId
);

export const selectAddInEntityId = createSelector(
  [selectDocumentMetadata],
  (properties) => properties.entityId
);

export const selectAddInEntityType = createSelector(
  [selectDocumentMetadata],
  (properties) => properties.entityType
);

export const selectCustomWorkspaceId = createSelector(
  [selectMetadataState],
  (metadata) => metadata.properties?.workspaceId
);

export const selectHasAllRequiredMetadata = createSelector(
  [selectDocumentMetadata],
  (properties) => {
    const isNillish = isEmpty(properties) || isNil(properties);
    if (isNillish) {
      return false;
    }

    return REQUIRED_METADATA_VALUES.every((key) => {
      return has(properties, key);
    });
  }
);

export const selectIsUnlinkedDocument = createSelector(
  [selectDocumentMetadata],
  (properties) => Boolean(properties?.unlinkedEntityId && !properties?.entityId)
);

export const selectUnlinkedEntityId = createSelector(
  [selectDocumentMetadata],
  (properties) => properties?.unlinkedEntityId ?? null
);

export const selectEffectiveWorkspaceId = createSelector(
  [selectCustomWorkspaceId, selectCurrentWorkspaceId],
  (metadataWorkspaceId, authWorkspaceId) =>
    metadataWorkspaceId ?? authWorkspaceId
);

export const selectIsSyncBlocked = createSelector(
  [selectDocumentMetadata],
  (properties) => Boolean(properties.isConverted || properties.isStale)
);

export const selectHasSaveTarget = createSelector(
  [selectDocumentMetadata],
  (properties) => {
    const { documentId, entityId, entityType, unlinkedEntityId } = properties;
    const isUnlinked = Boolean(unlinkedEntityId && !entityId);

    return isUnlinked
      ? Boolean(unlinkedEntityId)
      : Boolean(documentId && entityId && entityType);
  }
);
